import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import supabase, { supabaseAnonKey } from "../../../../utils/supabase";

const buildFunctionHeaders = () => {
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || supabaseAnonKey || "";
  if (!key) return undefined;
  return { apikey: key, Authorization: `Bearer ${key}` };
};

export default function GbifGroupedImportReview({ open, onClose, q = "", defaultDepth = 2, defaultGroupTopN = 5, defaultConcurrency = 3, roots = undefined }) {
  const [query, setQuery] = useState(q || "");
  const [depth, setDepth] = useState(defaultDepth);
  const [groupTopN, setGroupTopN] = useState(defaultGroupTopN);
  const [concurrency, setConcurrency] = useState(defaultConcurrency);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [selectedRoots, setSelectedRoots] = useState(() => new Set());

  useEffect(() => {
    setQuery(q || "");
    if (Array.isArray(roots) && roots.length) {
      setSelectedRoots(new Set(roots.map((r) => String(r))));
    }
  }, [q, roots]);

  const invokeGrouped = useCallback(async () => {
    setError("");
    if (!query || query.trim().length < 3) {
      setError("検索語（q）を3文字以上で指定してください。");
      return;
    }
    setLoading(true);
    try {
      const headers = buildFunctionHeaders();
      // If explicit roots are provided, ask the function to operate on those roots
      const body = Array.isArray(roots) && roots.length
        ? { roots: Array.from(roots).map((r) => String(r)), depth, groupTopN, concurrency }
        : { q: query, depth, groupTopN, concurrency };
      const { data, error: fnErr } = await supabase.functions.invoke("gbif-grouped-import", {
        headers,
        body,
      });
      if (fnErr) {
        setError(fnErr.message || "関数呼び出しに失敗しました。");
      } else {
        let resolved = data;
        if (typeof data === "string") {
          try {
            resolved = JSON.parse(data);
          } catch (_e) {
            // ignore
          }
        }
        setResult(resolved || null);
        // preselect all root keys by default if no explicit roots were given
        if (!Array.isArray(roots) || !roots.length) {
          const pre = new Set();
          (resolved?.groupsProcessed || []).forEach((g) => {
            const rkt = g.rootKeyToDesc || {};
            Object.keys(rkt).forEach((rk) => pre.add(rk));
          });
          setSelectedRoots(pre);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [query, depth, groupTopN, concurrency]);

  const toggleRoot = (rk) => {
    setSelectedRoots((prev) => {
      const next = new Set(prev);
      if (next.has(rk)) next.delete(rk);
      else next.add(rk);
      return next;
    });
  };

  const downloadPayload = () => {
    if (!result) return;
    const payload = { query: result.query, groupsProcessed: result.groupsProcessed };
    const prepared = { selectedRoots: Array.from(selectedRoots), payload };
    const blob = new Blob([JSON.stringify(prepared, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gbif_grouped_import_${(query || "q").replace(/\s+/g, "_")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyPayload = async () => {
    if (!result) return;
    const prepared = { selectedRoots: Array.from(selectedRoots), payload: { query: result.query, groupsProcessed: result.groupsProcessed } };
    try {
      await navigator.clipboard.writeText(JSON.stringify(prepared, null, 2));
    } catch (e) {
      // ignore
    }
  };

  const prepareTags = () => {
    if (!result) return null;
    const out = [];
    for (const g of result.groupsProcessed || []) {
      const tags = g.rootTags || {};
      for (const rk of Object.keys(tags)) {
        if (!selectedRoots.has(rk)) continue;
        const entry = { rootKey: rk, tags: tags[rk] };
        // ensure uniqueness of _gbif_synonym_of
        if (Array.isArray(entry.tags._gbif_synonym_of)) {
          entry.tags._gbif_synonym_of = Array.from(new Set(entry.tags._gbif_synonym_of.map(String)));
        }
        out.push(entry);
      }
    }
    return out;
  };

  return (
    <Dialog open={!!open} onClose={() => onClose?.()} maxWidth="lg" fullWidth>
      <DialogTitle>GBIF グループ化レビュー</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            指定したクエリで `gbif-grouped-import` を実行し、複数の GBIF ID にまたがる同一スペル名を検出します。
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <input value={query} onChange={(e) => setQuery(e.target.value)} style={{ flex: 1, padding: 8 }} />
            <Button variant="outlined" onClick={invokeGrouped} disabled={loading}>実行</Button>
            <Button variant="text" onClick={() => { setQuery(q || ""); }}>元に戻す</Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="caption">depth</Typography>
            <input type="number" value={depth} min={0} max={6} onChange={(e) => setDepth(Number(e.target.value))} style={{ width: 80 }} />
            <Typography variant="caption">groupTopN</Typography>
            <input type="number" value={groupTopN} min={1} max={50} onChange={(e) => setGroupTopN(Number(e.target.value))} style={{ width: 80 }} />
            <Typography variant="caption">concurrency</Typography>
            <input type="number" value={concurrency} min={1} max={10} onChange={(e) => setConcurrency(Number(e.target.value))} style={{ width: 80 }} />
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {loading && (
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          )}

          {!loading && result && (
            <Box>
              <Typography variant="subtitle2">クエリ: {String(result.query)}</Typography>
              <Typography variant="body2" color="text.secondary">Groups: {(result.groupsProcessed || []).length}</Typography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {(result.groupsProcessed || []).map((g, gi) => (
                  <Accordion key={gi} defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Typography sx={{ fontWeight: 600 }}>{g.group.canonical || g.group.k}</Typography>
                        <Typography variant="caption" color="text.secondary">family: {g.group.family || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">keys: {Array.isArray(g.group.keys)?g.group.keys.join(', '):''}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ mb: 1 }}>Duplicates: { (g.duplicates||[]).length }</Typography>
                      <List dense>
                        {(g.duplicates||[]).map((d, idx) => (
                          <ListItem key={idx}>
                            <ListItemText primary={d.name} secondary={`roots: ${d.roots.join(', ')}`} />
                          </ListItem>
                        ))}
                      </List>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ mb: 1 }}>Roots</Typography>
                      <Stack spacing={1}>
                        {Object.keys(g.rootKeyToDesc || {}).map((rk) => (
                          <Stack key={rk} direction="row" spacing={1} alignItems="center">
                            <Checkbox checked={selectedRoots.has(rk)} onChange={() => toggleRoot(rk)} />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{rk}</Typography>
                            <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                              {(g.rootKeyToDesc[rk]||[]).slice(0,8).map((n, i) => <Chip key={i} size="small" label={n} />)}
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { onClose?.(); }}>閉じる</Button>
        <Button onClick={downloadPayload} disabled={!result}>ダウンロード (JSON)</Button>
        <Button onClick={copyPayload} disabled={!result}>クリップボードにコピー</Button>
      </DialogActions>
    </Dialog>
  );
}
